import React, { useCallback, memo } from 'react';
import { Book } from '../../services/bookService';
import { Clock, GraduationCap, MessageCircle } from 'lucide-react';
import { useContact } from '../../hooks/useContact';
import { BASE_URL } from '../../apiConfig';

interface BookCardProps {
  book: Book;
  isActive?: boolean;
  isPartial?: boolean;
  onClick?: () => void;
  position?: 'left' | 'center' | 'right';
}

const BookCard: React.FC<BookCardProps> = ({ 
  book, 
  isActive = true, 
  isPartial = false, 
  onClick, 
  position = 'center' 
}) => {
  const { openWhatsApp, whatsappNumber, isLoading } = useContact();

  // Memoized image error handler
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/default-course.jpg';
  }, []);

  // Memoized book image source getter
  const getBookImageSrc = useCallback((book: Book): string => {
    if (!book.imagePath) {
      return '/default-course.jpg';
    }
    
    if (book.imagePath.startsWith('http')) {
      return book.imagePath;
    }
    
    let imagePath = book.imagePath;
    if (imagePath.startsWith('/')) {
      imagePath = imagePath.substring(1);
    }
    
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    return `${baseUrl}${imagePath}`;
  }, []);

  // Handle WhatsApp click
  const handleWhatsAppClick = useCallback(() => {
    if (whatsappNumber) {
      const message = `مرحباً، أريد معلومات عن كتاب: ${book.title}`;
      openWhatsApp(message);
    }
  }, [book.title, whatsappNumber, openWhatsApp]);

  // Grade translator function
  const getGradeInArabic = (grade: string): string => {
    const gradeTranslations: Record<string, string> = {
      'Secondary1': 'أولى ثانوي',
      'Secondary2': 'ثانية ثانوي',
      'Secondary3': 'ثالثة ثانوي',
      'default': grade
    };
    return gradeTranslations[grade] || grade;
  };

  // Grade color function (matching CourseCard)
  const getGradeColor = (grade: string): string => {
    const colors: Record<string, string> = {
      'Secondary1': 'bg-blue-100 text-blue-800',
      'Secondary2': 'bg-purple-100 text-purple-800',
      'Secondary3': 'bg-green-100 text-green-800',
      'default': 'bg-gray-100 text-gray-800'
    };
    return colors[grade] || colors['default'];
  };

  const cardClasses = `bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 w-80 mx-auto ${
    isActive 
      ? 'scale-100 z-20 opacity-100' 
      : isPartial 
      ? 'scale-90 z-10 opacity-70 hover:opacity-90 hover:scale-95' 
      : 'scale-75 z-0 opacity-50'
  } ${position === 'left' ? '-translate-x-4' : position === 'right' ? 'translate-x-4' : ''}`;

  return (
    <div 
      className={`${cardClasses} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Top Banner Section */}
      <div className="relative h-80 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-16 h-16 bg-white/20 rounded-full"></div>
          <div className="absolute top-8 right-8 w-12 h-12 bg-white/20 rounded-full"></div>
          <div className="absolute bottom-4 left-8 w-20 h-20 bg-white/20 rounded-full"></div>
        </div>
        
        {/* Book Image */}
        <div className="absolute inset-0">
          <img
            src={getBookImageSrc(book)}
            alt={book.title}
            className="w-full h-full object-contain bg-white"
            onError={handleImageError}
            loading="lazy"
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>
        
        {/* Grade Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getGradeColor(book.grade)}`}>
            {getGradeInArabic(book.grade)}
          </span>
        </div>
      </div>
      
      {/* Bottom White Card Section */}
      <div className="p-4 relative -mt-2 bg-white rounded-t-xl shadow-lg border border-gray-100 mx-4">
        {/* Main Book Title */}
        <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
          {book.title}
        </h3>
        
        {/* Book Description */}
        {book.description && (
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600 line-clamp-2">
              {book.description}
            </p>
            {book.description.length > 100 && (
              <button 
                className="text-blue-600 hover:text-blue-700 text-xs font-medium mt-2 hover:underline transition-colors duration-200"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const descElement = e.currentTarget.previousElementSibling;
                  if (descElement) {
                    descElement.classList.toggle('line-clamp-2');
                    e.currentTarget.textContent = descElement.classList.contains('line-clamp-2') ? 'عرض المزيد' : 'عرض أقل';
                  }
                }}
              >
                عرض المزيد
              </button>
            )}
            <div className="w-16 h-0.5 bg-green-400 mx-auto mt-2"></div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-3 mb-4">
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppClick}
            disabled={!whatsappNumber || isLoading}
            className={`w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-1.5 px-3 rounded-full shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5 group ${
              (!whatsappNumber || isLoading) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <MessageCircle className="w-3.5 h-3.5 group-hover:scale-110 transition-transform duration-300" />
            )}
            <span className="text-sm">تواصل عبر واتساب</span>
            <div className="w-1 h-1 bg-white rounded-full group-hover:animate-pulse"></div>
          </button>
        </div>
        
        {/* Book Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-xs text-gray-500">
            <GraduationCap className="w-3 h-3 mr-1.5 text-green-500" />
            <span>المستوى: {getGradeInArabic(book.grade)}</span>
          </div>
        </div>
        
        {/* Price Display */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-gray-100">
            <span className="text-xs text-gray-500 font-medium">السعر:</span>
            <span className="text-xl font-bold text-gray-900">
              {book.price === 0 ? 'مجاناً' : book.price?.toLocaleString('ar-EG') || 'غير محدد'}
            </span>
            {book.price && book.price !== 0 && (
              <span className="text-green-500 font-medium text-sm">جنيه</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(BookCard); 